import distinguishedFolderId from 'owa-service/lib/factory/distinguishedFolderId';
import type DistinguishedFolderIdName from 'owa-service/lib/contract/DistinguishedFolderIdName';
import type FindFolderRequest from 'owa-service/lib/contract/FindFolderRequest';
import type FindFolderResponseMessage from 'owa-service/lib/contract/FindFolderResponseMessage';
import findFolderOperation from 'owa-service/lib/operation/findFolderOperation';
import findFolderRequest from 'owa-service/lib/factory/findFolderRequest';
import folderResponseShape from 'owa-service/lib/factory/folderResponseShape';
import propertyUri from 'owa-service/lib/factory/propertyUri';
import { getJsonRequestHeader } from 'owa-service/lib/ServiceRequestUtils';
import extendedPropertyUri from 'owa-service/lib/factory/extendedPropertyUri';
import type BaseFolderId from 'owa-service/lib/contract/BaseFolderId';
import type FolderId from 'owa-service/lib/contract/FolderId';
import indexedPageView from 'owa-service/lib/factory/indexedPageView';
import type * as Schema from 'owa-graph-schema';
import type { MailboxRequestOptions } from 'owa-request-options-types';
import type { PropertyUris } from './PropertyUris';
import { REPLICA_LIST_EXTENDED_PROPERTY_TAG } from 'owa-folder-gql-mappers/lib/replicaListConstant';

const MAX_ENTRIES_RETURNED = 1000; // Same as server limit when indexedPageView not passed

function configureRequestBody(
    baseFolderIds: BaseFolderId[],
    indexedPageOffset: number,
    requestReplicaList?: boolean
): FindFolderRequest {
    const additionalProperties: PropertyUris[] = [
        propertyUri({ FieldURI: 'DistinguishedFolderId' }),
        propertyUri({ FieldURI: 'FolderClass' }),
        propertyUri({ FieldURI: 'FolderDisplayName' }),
        propertyUri({ FieldURI: 'ParentFolderId' }),
        propertyUri({ FieldURI: 'TotalCount' }),
        propertyUri({ FieldURI: 'UnreadCount' }),
    ];

    if (requestReplicaList) {
        additionalProperties.push(
            extendedPropertyUri({
                PropertyTag: REPLICA_LIST_EXTENDED_PROPERTY_TAG,
                PropertyType: 'Binary',
            })
        );
    }

    return findFolderRequest({
        FolderShape: folderResponseShape({
            BaseShape: 'IdOnly',
            AdditionalProperties: additionalProperties,
        }),
        Paging: indexedPageView({
            BasePoint: 'Beginning',
            Offset: indexedPageOffset,
            MaxEntriesReturned: MAX_ENTRIES_RETURNED,
        }),
        ParentFolderIds: baseFolderIds,
        ReturnParentFolder: true,
        ShapeName: 'Folder',
        Traversal: 'Deep',
    });
}

/**
 *
 * @param baseFolderDistinguishedFolderId base folder distinguished id for which we want to fetch the folder hierarchy
 * @param indexedPageOffset the offset from which to fetch
 * @param userIdentity the user identifier (smtp) that has permission to fetch/view these folders
 * @param mailboxSmtpAddress the smtp address of the mailbox where to route the service
 * @param mailboxType the mailbox type, currently we only fetch folders for UserMailbox types
 */
export default function findFolders(
    baseFolderDistinguishedFolderIds: string[],
    indexedPageOffset: number = 0,
    userIdentity?: string,
    mailboxSmtpAddress?: string,
    mailboxType: Schema.MailboxType = 'UserMailbox',
    requestReplicaList?: boolean
): Promise<FindFolderResponseMessage | undefined> {
    const folderIds = baseFolderDistinguishedFolderIds.map(f => {
        return distinguishedFolderId({
            Id: f as DistinguishedFolderIdName,
        }) as FolderId;
    });

    let requestBody = configureRequestBody(folderIds, indexedPageOffset, requestReplicaList);
    let requestOptions;

    if (userIdentity && mailboxSmtpAddress) {
        requestOptions = <MailboxRequestOptions>{
            mailboxInfo: {
                userIdentity: userIdentity,
                mailboxSmtpAddress: mailboxSmtpAddress,
                type: mailboxType,
            },
        };
    }

    return findFolderOperation(
        {
            Header: getJsonRequestHeader(),
            Body: requestBody,
        },
        requestOptions || undefined
    ).then(response => {
        if (mailboxType == 'SharedMailbox') {
            // In the FindFolderAction, the first folder is the principal's root node.
            // The second folder is the principal's Inbox.
            // For shared folder scenario, when the delegate has access to the principal's root,
            // we should load all the folders visible to the delegate under the principal's root.
            // When the delegate only has access to the principal's Inbox, but not the root, the response of the first folder will be null.
            if (
                response?.Body?.ResponseMessages?.Items?.[0] &&
                response.Body.ResponseMessages.Items[0].ResponseClass !== 'Success' &&
                response.Body.ResponseMessages.Items.length > 1
            ) {
                return response.Body.ResponseMessages.Items[1] as FindFolderResponseMessage;
            }
        }

        return response?.Body?.ResponseMessages?.Items?.[0] as
            | FindFolderResponseMessage
            | undefined;
    });
}
